import { set } from 'date-fns/set';

/**
 * Returns how many milliseconds into its day the given date sits.
 *
 * @param date - The date to reduce to its time components.
 * @returns The milliseconds elapsed since midnight.
 */
function millisecondsOfDay(date: Date): number {
  return (
    date.getHours() * 3_600_000 +
    date.getMinutes() * 60_000 +
    date.getSeconds() * 1_000 +
    date.getMilliseconds()
  );
}

/**
 * Apply the time components (hour, minute, second, and millisecond) from one date to another,
 * preserving the date of the second one.
 *
 * @param of - The date providing the time components to apply.
 * @param to - The date to which the time components will be applied.
 *
 * @returns A new date with the date components of `to` and the time components (hour, minute,
 * second, millisecond) from `of`.
 */
export function applyTime(of: Date, to: Date): Date {
  return set(to, {
    hours: of.getHours(),
    milliseconds: of.getMilliseconds(),
    minutes: of.getMinutes(),
    seconds: of.getSeconds(),
  });
}

/**
 * Check if a date is after another date, considering only their time components (hours, minutes,
 * seconds, and milliseconds).
 *
 * @param timeToCheck - The date representing the time to check.
 * @param referenceTime - The date representing the reference time.
 *
 * @returns Returns true if `timeToCheck` is after `referenceTime` based on time components only,
 * including milliseconds. Returns false otherwise.
 */
export function isTimeAfter(timeToCheck: Date, referenceTime: Date): boolean {
  return millisecondsOfDay(timeToCheck) > millisecondsOfDay(referenceTime);
}

/**
 * Check if a date is before another date, considering only their time components (hours, minutes,
 * seconds, and milliseconds).
 *
 * @param timeToCheck - The date representing the time to check.
 * @param referenceTime - The date representing the reference time.
 *
 * @returns Returns true if `timeToCheck` is before `referenceTime` based on time components only,
 * including milliseconds. Returns false otherwise.
 */
export function isTimeBefore(timeToCheck: Date, referenceTime: Date): boolean {
  return millisecondsOfDay(timeToCheck) < millisecondsOfDay(referenceTime);
}

/**
 * Generates a regular expression based on the provided date-fns format string to validate date and
 * time strings.
 *
 * @param timeFormat - The format string containing tokens to create a regular expression.
 * @returns A regular expression that matches the specified format.
 * @throws If an unsupported or invalid format token is encountered.
 */
export function timeFormatToRegex(timeFormat: string): RegExp {
  // Define a mapping of tokens to their regular expressions
  const tokenRegexMap: { [key: string]: string } = {
    yy: '[0-9]{2}',
    yyyy: '[0-9]{4}',
    M: '1[0-2]|[0-9]',
    MM: '1[0-2]|0[0-9]',
    d: '3[01]|[12][0-9]|[1-9]',
    dd: '3[01]|[12][0-9]|0[1-9]',
    H: '2[0-3]|1?[0-9]',
    HH: '2[0-3]|1[0-9]|0[0-9]',
    h: '1[0-2]|[0-9]',
    hh: '1[0-2]|0[0-9]',
    m: '[0-5]?[0-9]',
    mm: '[0-5][0-9]',
    s: '[0-5]?[0-9]',
    ss: '[0-5][0-9]',
    // date-fns renders `a` and `aa` uppercase; only `aaa` is lowercase.
    a: 'AM|PM',
    aa: 'AM|PM',
    aaa: 'am|pm',
  };

  // Replace tokens with their corresponding regular expressions
  const regexPattern = timeFormat.replace(/[yMdHhmsa]+/g, (match) => {
    const regex = tokenRegexMap[match];

    if (!regex) {
      throw new Error(`No regular expression defined for "${match}"`);
    }

    return '(' + regex + ')';
  });

  return new RegExp('^' + regexPattern + '$');
}
